import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Building2,
  ArrowLeft,
  Loader2,
  Save,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  Globe,
  Mail,
  Briefcase,
  Activity,
  MapPin,
  SlidersHorizontal,
  AlertCircle,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

import { editCompany, getCompanyById } from "@/services/superAdminService";
import { useProvisionCompany } from "@/components/super-admin/useProvisionCompany";
import ProvisionSubscriptionDialog from "@/components/super-admin/ProvisionSubscriptionDialog";
import { getAllSubscriptionPlans } from "@/services/subscriptionService";
import { Switch } from "@/components/ui/switch";

const EditCompany = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = location.state?.companyId || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState([]);
  /**
   * The raw getCompanyById payload, kept alongside the flattened `formData`.
   *
   * `formData` is a lossy projection built for this form's inputs — it drops
   * `available_plans`, `latest_subscription`, `statistics` and the company's
   * `state`, all of which the provisioning flow needs (the last one decides
   * whether GST is split CGST/SGST or charged as IGST). Rather than widen
   * formData with fields no input binds to, hold the response as it arrived.
   */
  const [companyData, setCompanyData] = useState(null);

  const [formData, setFormData] = useState({
    company_name: "",
    contact_email: "",
    phone: "",
    address: "",
    industry: "",
    status: "active",
    admin_name: "",
    admin_email: "",
    admin_id: "",
    plan_id: "",
    plan_name: "",
    billing_cycle: "monthly",
    // Declared here as well as written in initData — the Number of Employees
    // input binds to it on the first render, and without a seed that input
    // mounts uncontrolled and flips to controlled once the fetch lands.
    number_of_employees: 0,
    is_custom_course: null,
    custom_price_per_user: "",
    isPaidOffline: false,
    subscriptionStatus: "",
  });

  useEffect(() => {
    // Without the else branch a missing companyId left `loading` true forever,
    // so opening this page without navigation state (a refresh, a pasted URL)
    // showed the spinner and nothing else.
    if (companyId) {
      initData();
    } else {
      setError("No company ID provided.");
      setLoading(false);
    }
  }, [companyId]);

  const initData = async () => {
    try {
      setLoading(true);
      // Cleared so a successful retry leaves the error screen.
      setError("");
      const [companyRes, plansRes] = await Promise.all([
        getCompanyById(companyId),
        getAllSubscriptionPlans(),
      ]);

      const comp = companyRes.company;
      const admin = companyRes.admin_user;
      const sub = companyRes.subscription;
      setPlans(plansRes || []);
      // Feeds useProvisionCompany below — see the note on the state declaration.
      setCompanyData(companyRes);

      setFormData({
        company_name: comp.company_name || "",
        contact_email: comp.contact_email || "",
        phone: comp.phone || "",
        address: comp.address || "",
        industry: comp.industry || "",
        status: comp.status || "active",
        admin_name: admin?.admin_name || "",
        admin_email: admin?.admin_email || "",
        admin_id: admin?.admin_id || "",
        plan_id: sub?.plan_id || "",
        plan_name: sub?.plan_name || "",
        billing_cycle: sub?.billing_cycle || "monthly",
        number_of_employees: Number(sub?.number_of_employees) || 0,
        is_custom_course: sub?.is_custom_course ?? false,
        isPaidOffline: sub?.isPaidOffline || false,
        subscriptionStatus: sub?.status || "",
        custom_price_per_user:
          sub?.billing_cycle === "monthly"
            ? sub?.price_per_user_monthly || ""
            : sub?.price_per_user_annual || "",
      });
    } catch (error) {
      setError("Failed to load company data");
      toast.error("Error fetching details");
    } finally {
      setLoading(false);
    }
  };

  /**
   * The working replacement for this page's read-only Subscription Tier card.
   *
   * Same hook and same dialog CompanyDetails drives, so seats, plan, cycle and
   * negotiated rate are applied by the server through `provisionCompany` — with
   * its proration, GST, seat-floor and mid-term-downgrade rules — rather than
   * by this form's blanket `editCompany` PUT.
   *
   * Called unconditionally and above the `if (loading)` early return, as the
   * rules of hooks require. `onDone` is wrapped rather than passed by reference
   * because `initData` is a const declared above but the reference has to stay
   * late-bound to the current render's closure.
   */
  const provision = useProvisionCompany({
    company: companyData,
    companyId,
    onDone: () => initData(),
  });

  const handleInputChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.is_custom_course === true) {
      if (!formData.custom_price_per_user) {
        toast.error("Custom price per user is required");
        return false;
      }
    }
    try {
      setSaving(true);
      const payload = {
        company: {
          company_name: formData.company_name,
          contact_email: formData.contact_email,
          phone: formData.phone,
          address: formData.address,
          industry: formData.industry,
          status: formData.status,
        },
        admin_user: {
          admin_name: formData.admin_name,
          admin_email: formData.admin_email,
          admin_id: formData.admin_id,
        },
        subscription: {
          plan_id: formData.plan_id,
          plan_name: formData.plan_name,
          billing_cycle: formData.billing_cycle,
          is_custom_course: formData.is_custom_course,
          custom_price_per_user: formData.is_custom_course ? Number(formData.custom_price_per_user || 0) : 0,
          number_of_employees: formData.number_of_employees,
          subscriptionStatus: formData.subscriptionStatus,
        },
      };

      await editCompany(companyId, payload);
      toast.success("All changes saved successfully");
      navigate("/super-admin/companies");
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">
          Fetching records...
        </p>
      </div>
    );

  /**
   * `error` was set on a failed load but never rendered, so a company whose
   * fetch failed dropped straight through to the form — every field bound to
   * the empty initial state. Saving that PUT the blanks over the real record.
   * Block the form instead: there is nothing safe to edit without the data.
   */
  if (error)
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-12 text-center space-y-4">
            <AlertCircle className="h-14 w-14 mx-auto text-red-500" />
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-foreground">
                Could not load this company
              </h3>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <div className="flex items-center justify-center gap-3 pt-2">
              {companyId && (
                <Button onClick={() => initData()} variant="outline">
                  Try again
                </Button>
              )}
              <Button onClick={() => navigate("/super-admin/companies")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Companies
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto pb-20 px-4 sm:px-6 space-y-8">
      {/* --- STICKY HEADER --- */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-4 bg-background/80 backdrop-blur-md border-b mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-9 w-9"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl text-foreground font-bold tracking-tight">
                Edit Management Console
              </h1>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  • Modify organization and access
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="hidden sm:flex"
            >
              Discard
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Update Account
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* --- MAIN FORM AREA (8/12) --- */}
        <div className="lg:col-span-8 space-y-8">
          {/* Company Profile Section */}
          <Card className="overflow-hidden border-slate-200 dark:border-slate-800">
            <CardHeader className="bg-slate-50/50 dark:bg-slate-900/50 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Core Profile</CardTitle>
                  <CardDescription>
                    Primary identification and industry details
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Company Name
                  </Label>
                  <Input
                    placeholder="Enter company name"
                    value={formData.company_name}
                    onChange={(e) =>
                      handleInputChange("company_name", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Industry Sector
                  </Label>
                  <div className="relative">
                    <Select
                      value={formData.industry}
                      onValueChange={(value) =>
                        handleInputChange("industry", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "Maritime Industry",
                          "IT & Software",
                          "Manufacturing",
                          "Retail",
                          "Healthcare",
                          "Education",
                          "Finance",
                          "Construction",
                          "Other",
                        ].map((industry) => (
                          <SelectItem key={industry} value={industry}>
                            {industry}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Contact Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      value={formData.contact_email}
                      onChange={(e) =>
                        handleInputChange("contact_email", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Account Status
                  </Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => handleInputChange("status", v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-green-500" />{" "}
                          Active
                        </div>
                      </SelectItem>
                      <SelectItem value="inactive">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-slate-400" />{" "}
                          Inactive
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Headquarters Address
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Textarea
                    className="min-h-[100px] pl-9 pt-2.5"
                    placeholder="Physical location details..."
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin Details Section */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardHeader className="bg-slate-50/50 dark:bg-slate-900/50 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    Administrative Anchor
                  </CardTitle>
                  <CardDescription>
                    Primary contact person for system management
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Full Name
                </Label>
                <Input
                  value={formData.admin_name}
                  onChange={(e) =>
                    handleInputChange("admin_name", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Login/Admin Email
                </Label>
                <Input
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) =>
                    handleInputChange("admin_email", e.target.value)
                  }
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* --- SIDEBAR AREA (4/12) --- */}
        <div className="lg:col-span-4 space-y-6">
          {/* Subscription Section */}
          <Card className="border-primary/20 bg-gradient-to-b from-primary/5 to-transparent shadow-md overflow-hidden">
            <CardHeader>
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Subscription Tier</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/**
               * Read-only. Disabled three ways, deliberately — nothing in this
               * card is editable under any condition:
               *
               *  1. A bare `disabled` on every control. This was
               *     `disabled={formData.isPaidOffline}`, which left the whole
               *     card fully editable for OFFLINE companies — that flag is
               *     misnamed and is TRUE when the company pays ONLINE.
               *  2. `fieldset disabled` — disables descendant form controls
               *     natively, so a control added here later cannot come back
               *     interactive by someone forgetting the prop, and nothing is
               *     reachable by keyboard or tab.
               *  3. `pointer-events-none` — belt and braces for anything that
               *     drives itself from pointer events rather than the native
               *     disabled state.
               */}
              <fieldset
                disabled
                aria-disabled="true"
                className="space-y-4 opacity-60 pointer-events-none select-none"
              >
              <div className="p-4 rounded-xl bg-background border space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Subscription Status</Label>
                  <Switch
                    disabled
                    checked={
                      formData.subscriptionStatus === "active" ? true : false
                    }
                    onCheckedChange={(checked) =>
                      handleInputChange(
                        "subscriptionStatus",
                        checked ? "active" : "paused",
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Current Selection
                  </Label>
                  <Select
                    disabled
                    value={
                      formData.plan_id
                        ? `${formData.plan_id}-${formData.billing_cycle}`
                        : ""
                    }
                    onValueChange={(value) => {
                      const parts = value.split("-");
                      const bc = parts.pop();
                      const pid = parts.join("-");
                      const selectedPlan = plans.find((p) => p.plan_id === pid);
                      setFormData((prev) => ({
                        ...prev,
                        plan_id: pid,
                        plan_name: selectedPlan?.plan_name || "",
                        billing_cycle: bc,
                      }));
                    }}
                  >
                    <SelectTrigger className="h-12 border-primary/20 hover:border-primary/50 transition-colors">
                      <SelectValue placeholder="Change Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <React.Fragment key={plan.plan_id}>
                          <SelectItem value={`${plan.plan_id}-monthly`}>
                            <div className="flex flex-col py-1">
                              <span className="font-semibold">
                                {plan.plan_name} (Monthly)
                              </span>
                              <span className="text-[10px] text-muted-foreground">
                                ₹{plan.price_per_user_monthly}/user
                              </span>
                            </div>
                          </SelectItem>
                          {!plan.is_trial && (
                            <SelectItem value={`${plan.plan_id}-annual`}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold">
                                  {plan.plan_name} (Annual)
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  ₹{plan.price_per_user_annual}/user
                                </span>
                              </div>
                            </SelectItem>
                          )}
                        </React.Fragment>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      Billing Cycle:
                    </span>
                    <Badge variant="outline" className="capitalize">
                      {formData.billing_cycle}
                    </Badge>
                  </div>
                </div>

                {/* Custom Price */}
                <div>
                  <Label> Number of Employees</Label>
                  <Input
                    disabled
                    type="number"
                    min={1}
                    onWheel={(e) => e.target.blur()}
                    value={formData.number_of_employees}
                    onChange={(e) =>
                      handleInputChange("number_of_employees", e.target.value)
                    }
                    placeholder="e.g., 199"
                    className="[&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {/* Custom Course Radio */}
                <div>
                  <Label>Custom Pricing *</Label>
                  <div className="flex items-center gap-6 mt-2">
                    {/* YES */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        disabled
                        type="radio"
                        name="custom_course"
                        value="yes"
                        checked={formData.is_custom_course === true}
                        onChange={() =>
                          handleInputChange("is_custom_course", true)
                        }
                        className="accent-primary"
                      />
                      <span className="text-sm text-foreground">
                        Yes
                      </span>
                    </label>

                    {/* NO */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        disabled
                        type="radio"
                        name="custom_course"
                        value="no"
                        checked={formData.is_custom_course === false}
                        onChange={() => {
                          handleInputChange("is_custom_course", false);
                          handleInputChange("custom_price_per_user", 0);
                        }}
                        className="accent-primary"
                      />
                      <span className="text-sm text-foreground">
                        No
                      </span>
                    </label>
                  </div>
                </div>
                {formData.is_custom_course ? (
                  <div>
                    <Label> Price Per User *</Label>
                    <Input
                      disabled
                      type="number"
                      min={1}
                      onWheel={(e) => e.target.blur()}
                      value={formData.custom_price_per_user}
                      onChange={(e) =>
                        handleInputChange(
                          "custom_price_per_user",
                          e.target.value,
                        )
                      }
                      placeholder="e.g., 199"
                      className="[&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Price per user ({formData.billing_cycle})
                    </p>
                  </div>
                ) : (
                  <div /> // keeps grid alignment when hidden
                )}
              </div>
              </fieldset>

              {/* Outside the fieldset so it stays at full contrast — this is
                  the only part of the card still worth reading, and the button
                  must stay clickable while everything above it is disabled. */}
              {provision.canProvision ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                  <p className="text-[10px] text-amber-800 dark:text-amber-300 font-medium leading-relaxed">
                    The fields above are read-only. Change the plan, seats,
                    billing cycle or negotiated rate here instead — it applies
                    immediately and is recorded against this company's request
                    history. No payment is collected.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => provision.openProvision()}
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
                    Manage subscription
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg bg-muted p-3 border">
                  <p className="text-[10px] text-muted-foreground font-medium leading-relaxed">
                    {/* An online company's seats and plan are driven by its
                        Razorpay mandate — provisioning is refused server-side,
                        so no button is offered. Matches the note CompanyDetails
                        shows in the same situation. */}
                    {provision.isPaidOnline
                      ? "Read-only. This company pays online via Razorpay — its seats and plan are driven by its mandate, and it must change them from its own subscription page."
                      : "Read-only. This card shows the current subscription; it can no longer be changed from here."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statistics Section */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold">
                  Usage Summary
                </CardTitle>
              </div>
            </CardHeader>
            {/* Both figures were hardcoded "01" and "02" — every company read
                the same, and the second row was labelled "System Admins", a
                count the API does not return. `statistics` carries exactly two
                numbers; show those, under the names CompanyDetails gives them. */}
            <CardContent className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  Employees
                </div>
                <span className="text-sm font-bold">
                  {Number(companyData?.statistics?.employee_count) || 0}
                </span>
              </div>
              <Separator className="opacity-50" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  Login accounts
                </div>
                <span className="text-sm font-bold">
                  {Number(companyData?.statistics?.user_count) || 0}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Same dialog CompanyDetails renders — one implementation, two pages. */}
      <ProvisionSubscriptionDialog company={companyData} provision={provision} />
    </div>
  );
};

export default EditCompany;
